<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatSelectOptions } from '@phoenix/helpers/form-helper';
import { PhxIcon } from '@phx-styleguides/jlc';
import EngravingToolSummary from '@maison/components/engravingTool/checkout/EngravingToolSummary.vue';
import EngravingToolContactInformation from '@maison/components/engravingTool/checkout/EngravingToolContactInformation.vue';
import EngravingToolContactPoint from '@maison/components/engravingTool/checkout/EngravingToolContactPoint.vue';
import ProductCard from '@maison/components/product/ProductCard.vue';

const props = defineProps({
  homepageUrl: {
    type: String,
    required: true,
  },
  reference: {
    type: String,
    required: true,
  },
  referenceType: {
    type: String,
    required: true,
  },
  configuration: {
    type: Object,
    required: true,
  },
  article: {
    type: Object,
    required: true,
  },
  summary: {
    type: Object,
    required: true,
  },
  form: {
    type: Object,
    required: true,
  },
  resultFile: {
    type: File,
    required: true,
  },
  resultPreview: {
    type: File,
    required: true,
  },
  trackingData: {
    type: Object,
    required: true,
  },
  phoneNumber: {
    type: String,
    default: null,
  },
  successModalId: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['back']);

const { t } = useI18n();

const contactInformationRef = ref(null);
const fields = ref({
  ...props.form.fields,
  ...{
    country: {
      id: 'country',
      name: 'country',
      label: t('Form.label.country'),
      value: null,
      options: props.form.countries,
      required: true,
      type: 'select',
    },
    title: {
      id: 'title',
      name: 'title',
      label: t('Form.label.title'),
      value: null,
      options: formatSelectOptions(['mr', 'ms', 'miss']),
      required: true,
      type: 'select',
    },
    firstName: {
      id: 'firstName',
      name: 'firstName',
      label: t('Form.label.firstName'),
      value: '',
      required: true,
    },
    lastName: {
      id: 'lastName',
      name: 'lastName',
      label: t('Form.label.lastName'),
      value: '',
      required: true,
    },
    city: {
      id: 'city',
      name: 'city',
      label: t('Form.label.city'),
      value: '',
      required: true,
    },
    zip: {
      id: 'zip',
      name: 'zip',
      label: t('Form.label.zip'),
      value: '',
      required: true,
      pattern: '.*',
    },
    email: {
      id: 'email',
      name: 'email',
      label: t('Form.label.email'),
      value: '',
      required: true,
      type: 'email',
    },
    phone: {
      id: 'phone',
      name: 'phone',
      label: t('Form.label.phone'),
      value: '',
      required: true,
      type: 'phone',
    },
    contactType: {
      id: 'contactType',
      name: 'contactType',
      label: t('Form.label.contactType'),
      value: 'boutique',
      type: 'radio',
      required: true,
      options: [
        {
          id: 'contactType_boutique',
          value: 'boutique',
          label: t('Form.contactType.boutique'),
        },
        {
          id: 'contactType_personalAdvisor',
          value: 'personalAdvisor',
          label: t('Form.contactType.personalAdvisor'),
        },
      ],
    },
    contactMode: {
      id: 'contactMode',
      name: 'contactMode',
      label: '',
      value: [],
      type: 'checkboxList',
      required: true,
      options: [
        {
          id: 'contactMode_email',
          value: 'email',
          label: t('Form.contactMode.email'),
        },
        {
          id: 'contactMode_phone',
          value: 'phone',
          label: t('Form.contactMode.phone'),
        },
      ],
    },
    boutique: {
      id: 'boutique',
      name: 'boutique',
      label: t('Form.label.boutique'),
      value: null,
      type: 'radio',
      required: true,
      options: [],
    },
    reference: {
      id: 'reference',
      name: 'reference',
      value: props.reference,
    },
    referenceType: {
      id: 'referenceType',
      name: 'referenceType',
      value: props.referenceType,
    },
    configuration: {
      id: 'configuration',
      name: 'configuration',
      // We can't correctly post JSON data with the Content-Type "multipart/form-data"
      value: new Blob([JSON.stringify(props.configuration)], {
        type: 'application/json',
      }),
      type: 'file',
    },
    resultFile: {
      id: 'resultFile',
      name: 'resultFile',
      value: props.resultFile,
      type: 'file',
    },
    resultPreview: {
      id: 'resultPreview',
      name: 'resultPreview',
      value: props.resultPreview,
      type: 'file',
    },
    partner: {
      id: 'partner',
      name: 'partner',
      label: t('Form.label.partner'),
      value: false,
      type: 'checkbox',
    },
    partnerCountry: {
      id: 'partnerCountry',
      name: 'partnerCountry',
      label: t('Form.label.country'),
      value: null,
      options: props.form.countries,
      required: true,
      type: 'select',
    },
    partnerName: {
      id: 'partnerName',
      name: 'partnerName',
      label: t('Form.label.name'),
      value: '',
      required: true,
    },
    partnerCity: {
      id: 'partnerCity',
      name: 'partnerCity',
      label: t('Form.label.city'),
      value: '',
      required: true,
    },
    partnerEmail: {
      id: 'partnerEmail',
      name: 'partnerEmail',
      label: t('Form.label.email'),
      value: '',
      required: true,
      type: 'email',
    },
  },
});

const hasPhoneNumber = computed(() => props.phoneNumber !== null);

const back = () => {
  emit('back');
};

const beforeSubmit = async () => {
  window.tracking.pushEvent(
    window.tracking.getEngravingReceiveMyEstimateTracking(props.trackingData.itemId, props.trackingData.itemName),
  );

  return await contactInformationRef.value.submit();
};

watch(
  () => fields.value.partner.value,
  (value) => {
    fields.value.contactMode.label = value ? t('Form.label.partnerContactMode') : t('Form.label.contactMode');
  },
  { immediate: true },
);
</script>

<template>
  <nav class="navbar navbar--checkout" :aria-label="$t('Accessibility.checkoutNavigation')">
    <div class="navbar-container">
      <div class="navbar__section navbar__section--top">
        <div class="navbar__section-wrapper container">
          <ul class="navbar__items navbar__items-primary">
            <li class="navbar__item">
              <button class="navbar__button uppercase" :title="$t('EngravingTool.editYourEngraving')" @click="back">
                <phx-icon icon="chevron-left-16" />
                <span>{{ $t('EngravingTool.editYourEngraving') }}</span>
              </button>
            </li>

            <li class="navbar__item navbar__logo">
              <a
                v-if="homepageUrl"
                :href="homepageUrl"
                class="navbar__button"
                :title="$t('Accessibility.navbarLogoAlt')"
              >
                <phx-icon icon="logo" />
              </a>
              <phx-icon v-else icon="logo" />
            </li>

            <li class="navbar__item">
              <a
                v-if="hasPhoneNumber"
                :href="`tel:${phoneNumber}`"
                class="navbar__button"
                :title="$t('Accessibility.navbarTelephone')"
              >
                <phx-icon icon="smartphone-16" />
                <span dir="ltr">{{ phoneNumber }}</span>
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </nav>

  <main id="main-content">
    <div class="content content--boxed">
      <div class="container container--responsive">
        <h2 class="content__title">{{ $t('EngravingTool.checkout.title') }}</h2>

        <div class="checkout">
          <div class="ecom-content">
            <section class="ecom-section">
              <header class="checkout-section__header ecom-section__header ecom-section__header--sticky">
                <h3 class="ecom-section__title">{{ $t('EngravingTool.checkout.yourEngraving') }}</h3>
              </header>

              <div class="ecom-section__body">
                <ul class="product-card-list product-card-list--with-border">
                  <li class="product-card-list__entry">
                    <product-card
                      orientation="horizontal"
                      size="large"
                      :image="article.image"
                      :collection="article.title"
                      :should-display-add-to-wishlist="false"
                    >
                      <template #description>
                        <div v-for="option in article.options" :key="option.key">
                          <span>{{ option.key }}: </span>
                          <span>{{ option.value }}</span>
                        </div>
                      </template>
                    </product-card>
                  </li>
                </ul>

                <div class="lg:hidden">
                  <hr class="divider" />
                  <engraving-tool-summary v-bind="summary" />
                  <hr class="my-base" />
                </div>
              </div>
            </section>

            <section class="ecom-section">
              <header class="checkout-section__header ecom-section__header ecom-section__header--sticky">
                <h3 class="ecom-section__title">{{ $t('EngravingTool.checkout.contactInformation') }}</h3>
              </header>

              <div class="ecom-section__body" data-cy="contact-information">
                <engraving-tool-contact-information ref="contactInformationRef" v-model="fields" />
              </div>
            </section>

            <section class="ecom-section" data-cy="contact-point">
              <header class="checkout-section__header ecom-section__header ecom-section__header--sticky">
                <h3 class="ecom-section__title">{{ $t('EngravingTool.checkout.contactPoint') }}</h3>
              </header>

              <div class="ecom-section__body">
                <engraving-tool-contact-point
                  v-model="fields"
                  :form="form"
                  :before-submit="beforeSubmit"
                  :success-modal-id="successModalId"
                />
              </div>
            </section>
          </div>

          <aside class="checkout__aside" data-sticky>
            <section class="ecom-section">
              <header class="checkout-section__header ecom-section__header ecom-section__header--sticky">
                <h3 class="ecom-section__title">{{ $t('Order.summary') }}</h3>
              </header>

              <div class="ecom-section__body">
                <engraving-tool-summary v-bind="summary" />
              </div>
            </section>
          </aside>
        </div>
      </div>
    </div>
  </main>
</template>
